// ------------------------------------------------------------------
// ResponsivePreview — story 2-147p (P10 §4, DOD). Shows the SAME content at
// several simulated breakpoints side by side, inline in the gallery — the
// same "bounded, fixed-width demo box" idiom `DesignSystemPage.tsx` already
// hand-rolls in a few places (a 390px-wide, max-width-full mobile-sim box
// for the content-stress matrix; per-viewport Modal placement notes) — one
// reusable component instead of a bespoke box per call site.
//
// Widths are a PROP/variable read at the JSX call site
// (`style={{ width: `${bp.width}px` }}`), never a literal stated directly
// in a className or style attribute — the exact "reading a token/variable,
// not stating a number" shape `check-shell-geometry.mjs`'s own header
// comment carves out as compliant ("A component READING a token... is not
// a violation — only a component STATING the number itself is"). The
// default breakpoint set's own literal numbers live in a plain TS const
// (`DEFAULT_RESPONSIVE_PREVIEW_BREAKPOINTS` below), not inside a JSX
// className/style attribute, so this file needed no
// check-shell-geometry.mjs allowlist entry — confirmed by a clean run
// (`node scripts/check-shell-geometry.mjs`) after this file was added.
//
// Story 2-147xd: outer wrapper composes `Cluster` (`src/components/ui/
// Layout.tsx`, story 2-147sa) instead of a hand-rolled `flex flex-wrap
// gap-4` — the exact "independent items that reflow as a group" shape
// Cluster exists for (each breakpoint frame stays whole, the row wraps).
// The inner per-breakpoint `space-y-2` stack stays a plain className (no
// flex/grid token alongside it, so it was never a tracked violation).
// ------------------------------------------------------------------

import { Cluster } from '../ui/Layout';

export interface ResponsivePreviewBreakpoint {
  key: string;
  label: string;
  /** Simulated viewport width in px. */
  width: number;
}

// Mirrors the three widths `docs/prd/10-design-system.md` §2.6 already
// verifies content against tree-wide (390 / 768 / … up to 1920) — narrowed
// to the three most load-bearing for an inline side-by-side comparison.
export const DEFAULT_RESPONSIVE_PREVIEW_BREAKPOINTS: ResponsivePreviewBreakpoint[] = [
  { key: 'mobile', label: 'Mobile · 390px', width: 390 },
  { key: 'tablet', label: 'Tablet · 768px', width: 768 },
  { key: 'desktop', label: 'Desktop · 1024px', width: 1024 },
];

export interface ResponsivePreviewProps {
  breakpoints?: ResponsivePreviewBreakpoint[];
  /** The SAME content, re-rendered inside each simulated-width frame. */
  children: React.ReactNode;
}

export function ResponsivePreview({
  breakpoints = DEFAULT_RESPONSIVE_PREVIEW_BREAKPOINTS,
  children,
}: ResponsivePreviewProps): JSX.Element {
  return (
    <Cluster gap={4} align="stretch" data-testid="responsive-preview">
      {breakpoints.map((bp) => (
        <div key={bp.key} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{bp.label}</p>
          <div
            className="max-w-full overflow-hidden rounded-lg border border-dashed border-border bg-card p-3"
            style={{ width: `${bp.width}px` }}
            data-testid={`responsive-preview-${bp.key}`}
          >
            {children}
          </div>
        </div>
      ))}
    </Cluster>
  );
}
